
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, {useRef} from 'react';
import {BrandIdentity, ImageFile} from '../types';
import {XMarkIcon, SparklesIcon, PlusIcon, MegaphoneIcon} from './icons';

interface BrandKitProps {
  config: BrandIdentity;
  onChange: (config: BrandIdentity) => void;
  isOpen: boolean;
  onClose: () => void;
}

const BrandKit: React.FC<BrandKitProps> = ({config, onChange, isOpen, onClose}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && config.referenceImages.length < 2) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        const newImg: ImageFile = {file, base64};
        onChange({...config, referenceImages: [...config.referenceImages, newImg]});
      };
      reader.readAsDataURL(file);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-80 bg-gray-900/95 backdrop-blur-xl border-l border-gray-700 z-40 shadow-2xl flex flex-col transform transition-transform duration-300">
      <div className="p-6 border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-indigo-400" />
          <h3 className="font-bold text-lg text-white">Brand Identity</h3>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-gray-800 rounded-full transition-colors">
          <XMarkIcon className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      <div className="p-6 flex-grow overflow-y-auto space-y-8">
        <div className="flex items-center justify-between bg-gray-800/50 p-3 rounded-xl border border-gray-700">
          <div className="flex flex-col">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Global Status</span>
            <span className={config.isActive ? "text-indigo-400 text-sm font-semibold" : "text-gray-500 text-sm"}>
              {config.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <button 
            onClick={() => onChange({...config, isActive: !config.isActive})}
            className={`w-12 h-6 rounded-full relative transition-colors ${config.isActive ? 'bg-indigo-600' : 'bg-gray-600'}`}
          >
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${config.isActive ? 'right-1' : 'left-1'}`} />
          </button>
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-2">System Instructions</label>
          <textarea
            value={config.systemInstruction}
            onChange={(e) => onChange({...config, systemInstruction: e.target.value})}
            placeholder="e.g. Always use moody cinematic lighting and high grain..."
            className="w-full h-32 bg-gray-800 border border-gray-700 rounded-xl p-3 text-sm focus:ring-1 focus:ring-indigo-500 focus:outline-none placeholder-gray-600 resize-none text-gray-200"
          />
          <p className="text-[10px] text-gray-500 mt-2 italic flex items-start gap-1">
            <MegaphoneIcon className="w-3 h-3 shrink-0" />
            These instructions persist across all generations and extensions.
          </p>
        </div>

        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-3">Persistent Style Assets ({config.referenceImages.length}/2)</label>
          <div className="grid grid-cols-2 gap-3">
            {config.referenceImages.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-gray-700 group">
                <img src={URL.createObjectURL(img.file)} className="w-full h-full object-cover" />
                <button 
                  onClick={() => onChange({...config, referenceImages: config.referenceImages.filter((_, idx) => idx !== i)})}
                  className="absolute top-1 right-1 bg-black/60 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <XMarkIcon className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
            {config.referenceImages.length < 2 && (
              <button 
                onClick={() => inputRef.current?.click()}
                className="aspect-square border-2 border-dashed border-gray-700 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:text-indigo-400 hover:border-indigo-400/50 transition-all bg-gray-800/30"
              >
                <PlusIcon className="w-6 h-6" />
                <span className="text-[10px] mt-1">Add Style</span>
              </button>
            )}
          </div>
          <input ref={inputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
        </div>
      </div>

      <div className="p-6 bg-gray-800/30 border-t border-gray-700">
        <p className="text-[10px] text-gray-500 text-center uppercase tracking-tighter">
          Assets here stay throughout all extensions
        </p>
      </div>
    </div>
  );
};

export default BrandKit;
